import api from '@/services/api';
import type {
    AdminUser,
    ListParams,
    UserListResponse,
    UserResponse,
} from '../types/users.types';
import type {
    CreateUserFormInputs,
    UpdateUserFormInputs,
} from '../schemas/user.schema';

const usersPath = '/auth/users';

const toUserUrl = (id: string): string => `${usersPath}/${id}`;

// Auth-service response shape varies: index/show return { success, data, meta? };
// store/update/admin-actions return { data } or { message }; destroy returns 204.
// Axios throws on non-2xx, so HTTP status is the success signal — we don't need
// a body-level `success` boolean in places where the backend doesn't emit one.

export const fetchUsers = async (params: ListParams = {}): Promise<UserListResponse> => {
    const response = await api.get<UserListResponse>(`${usersPath}/`, { params });
    return response.data;
};

export const fetchUserById = async (id: string): Promise<AdminUser> => {
    const response = await api.get<UserResponse>(toUserUrl(id));
    return response.data.data;
};

export const createUser = async (payload: CreateUserFormInputs): Promise<AdminUser> => {
    const response = await api.post<UserResponse>(`${usersPath}/`, payload);
    return response.data.data;
};

export const updateUser = async (id: string, payload: UpdateUserFormInputs): Promise<AdminUser> => {
    const response = await api.put<UserResponse>(toUserUrl(id), payload);
    return response.data.data;
};

export const deleteUser = async (id: string): Promise<void> => {
    await api.delete<void>(toUserUrl(id));
};

export const deactivateUser = async (id: string): Promise<AdminUser> => {
    const response = await api.post<UserResponse>(`${toUserUrl(id)}/deactivate`);
    return response.data.data;
};

export const reactivateUser = async (id: string): Promise<AdminUser> => {
    const response = await api.post<UserResponse>(`${toUserUrl(id)}/reactivate`);
    return response.data.data;
};

export const resetUserMfa = async (id: string): Promise<AdminUser> => {
    const response = await api.post<UserResponse>(`${toUserUrl(id)}/mfa/reset`);
    return response.data.data;
};

export const revokeUserTrustedDevices = async (id: string): Promise<void> => {
    await api.delete<{ message: string }>(`${toUserUrl(id)}/trusted-devices`);
};

export const revokeUserSessions = async (id: string): Promise<void> => {
    await api.post<{ message: string }>(`${toUserUrl(id)}/sessions/revoke`);
};

export const toggleUserMfaEnforcement = async (id: string, enforce: boolean): Promise<AdminUser> => {
    const response = await api.patch<UserResponse>(`${toUserUrl(id)}/mfa/enforcement`, { enforce });
    return response.data.data;
};

export const setUserAssociation = async (
    id: string,
    payload: { dealer_id: string | null; organisation_id: string | null },
): Promise<AdminUser> => {
    const response = await api.put<UserResponse>(`${toUserUrl(id)}/association`, payload);
    return response.data.data;
};

export const releaseUserEmail = async (id: string, confirmation: string): Promise<AdminUser> => {
    const response = await api.post<UserResponse>(`${toUserUrl(id)}/email/release`, {
        confirmation,
    });
    return response.data.data;
};
